import { useCallback, useEffect, useState, type FormEvent } from 'react';
import { DateRangeField } from './DateRangeField';
import { getErrorMessage } from '../api/client';
import { getSalesConsumptionPostingDetail, getSalesConsumptionPostings, reverseSalesConsumptionPosting } from '../api/inventory';
import { useAuth } from '../auth/AuthProvider';
import { getStoreScopeOptions } from '../auth/permissions';
import { Modal } from './Modal';
import type {
  SalesConsumptionPostingListFilters,
  SalesConsumptionPostingListItem,
  SalesPostingDetailResponse,
  SalesPostingRunStatus,
  SalesPostingReversalResult,
  SalesPostingTransactionReversalStatus,
} from '../types/inventory';
import type { Store } from '../types/stores';
import { DEFAULT_STORE_TIMEZONE, resolveStoreTimezone } from '../utils/timezones';
import { formatOptionalMoney, formatQuantity } from '../utils/costFormatting';

const PAGE_SIZE = 20;
const REVERSAL_CONFIRM_WORD = 'REVERSAR';
const MIN_REASON_LENGTH = 10;

export function SalesPostingHistoryPanel({ stores }: { stores: Store[] }) {
  const { user } = useAuth();
  const [filters, setFilters] = useState<SalesConsumptionPostingListFilters>({});
  const [items, setItems] = useState<SalesConsumptionPostingListItem[]>([]);
  const [page, setPage] = useState({ total: 0, offset: 0, hasMore: false });
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string>();
  const [openPostingId, setOpenPostingId] = useState<string>();

  const load = useCallback(async (nextFilters: SalesConsumptionPostingListFilters, offset = 0) => {
    setIsLoading(true);
    setError(undefined);
    try {
      const result = await getSalesConsumptionPostings({ ...nextFilters, limit: PAGE_SIZE, offset });
      setItems(result.items);
      setPage({ total: result.total, offset: result.offset, hasMore: result.hasMore });
    } catch (loadError) {
      setError(getErrorMessage(loadError, 'No fue posible cargar el historial de descuentos.'));
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    const { defaultStoreId } = getStoreScopeOptions(user?.role, stores);
    const initialFilters: SalesConsumptionPostingListFilters = defaultStoreId ? { storeId: defaultStoreId } : {};
    setFilters(initialFilters);
    void load(initialFilters);
  }, [load, stores, user?.role]);

  const submit = (event: FormEvent) => {
    event.preventDefault();
    void load(filters);
  };

  const refreshAfterReversal = () => { void load(filters, page.offset); };

  return <section className="preview-section posting-history-section">
    <div className="preview-section__heading"><div><h3>Historial de descuentos</h3><span>Corridas de descuento real de inventario por ventas y sus reversas.</span></div></div>
    {error && <div className="alert alert--error">{error}</div>}

    <form className="stock-filters" onSubmit={submit}>
      <label className="field"><span>Tienda</span><select onChange={(event) => setFilters({ ...filters, storeId: event.target.value || undefined })} value={filters.storeId || ''}>{getStoreScopeOptions(user?.role, stores).allowsAllStores && <option value="">Todas las tiendas</option>}{stores.map((store) => <option key={store.id} value={store.id}>{store.name}</option>)}</select></label>
      <label className="field"><span>Estado</span><select onChange={(event) => setFilters({ ...filters, status: (event.target.value || undefined) as SalesPostingRunStatus | undefined })} value={filters.status || ''}>
        <option value="">Todos</option>
        <option value="COMPLETED">Aplicado</option>
        <option value="REVERSED">Revertido</option>
        <option value="FAILED">Fallido</option>
      </select></label>
      <DateRangeField from={filters.dateFrom || ''} to={filters.dateTo || ''} onChange={(from, to) => setFilters({ ...filters, dateFrom: from || undefined, dateTo: to || undefined })} />
      <button className="button button--primary" disabled={isLoading} type="submit">{isLoading ? 'Cargando...' : 'Aplicar filtros'}</button>
    </form>

    {isLoading ? <div className="inline-loading"><span className="spinner" />Cargando historial...</div> : <PostingListTable items={items} onOpen={setOpenPostingId} stores={stores} />}
    {!isLoading && page.total > 0 && <div className="coverage-pagination">
      <span>Mostrando {page.offset + 1}–{page.offset + items.length} de {page.total}</span>
      <div>
        <button className="button button--ghost" disabled={page.offset === 0} onClick={() => void load(filters, Math.max(0, page.offset - PAGE_SIZE))} type="button">Anterior</button>
        <button className="button button--ghost" disabled={!page.hasMore} onClick={() => void load(filters, page.offset + PAGE_SIZE)} type="button">Siguiente</button>
      </div>
    </div>}

    {openPostingId && <PostingDetailModal postingRunId={openPostingId} onClose={() => setOpenPostingId(undefined)} onReversed={refreshAfterReversal} stores={stores} />}
  </section>;
}

function PostingListTable({ items, onOpen, stores }: { items: SalesConsumptionPostingListItem[]; onOpen: (id: string) => void; stores: Store[] }) {
  if (!items.length) return <div className="empty-panel empty-panel--small"><strong>Todavía no hay corridas de descuento</strong><span>Aparecerán aquí cuando confirmes un descuento real desde la simulación.</span></div>;
  return <div className="data-table-wrap"><table className="data-table posting-history-table"><thead><tr><th>Fecha de ejecución</th><th>Rango</th><th>Tienda</th><th>Estado</th><th>Tickets analizados</th><th>Líneas de venta</th><th>Movimientos creados</th><th>Costo real descontado</th><th>Usuario</th><th>Acción</th></tr></thead><tbody>{items.map((item) => <tr key={item.id}>
    <td>{formatDateTime(item.createdAt, resolveStoreTimezone(stores, { storeId: item.storeId }))}</td>
    <td>{formatDateOnly(item.dateFrom)} – {formatDateOnly(item.dateTo)}</td>
    <td>{item.storeName}</td>
    <td><PostingStatusPill status={item.status} /></td>
    <td>{item.totalReceiptsAnalyzed}</td>
    <td>{item.totalSaleLinesAnalyzed}</td>
    <td>{item.totalTransactionsCreated}</td>
    <td>{formatOptionalMoney(item.totalRealCostDeducted)}</td>
    <td>{item.createdByName || 'Sin usuario'}</td>
    <td><button className="text-button" onClick={() => onOpen(item.id)} type="button">Ver detalle</button></td>
  </tr>)}</tbody></table></div>;
}

function PostingDetailModal({ postingRunId, onClose, onReversed, stores }: { postingRunId: string; onClose: () => void; onReversed: () => void; stores: Store[] }) {
  const [detail, setDetail] = useState<SalesPostingDetailResponse>();
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string>();
  // La corrida no trae storeId, solo storeName; se busca por nombre en la lista de tiendas ya
  // cargada (mejor esfuerzo — si hay dos tiendas con el mismo nombre, cae al default).
  const timeZone = detail ? stores.find((store) => store.name === detail.posting.storeName)?.timezone ?? DEFAULT_STORE_TIMEZONE : DEFAULT_STORE_TIMEZONE;
  const [isReversalModalOpen, setIsReversalModalOpen] = useState(false);
  const [reversalResult, setReversalResult] = useState<SalesPostingReversalResult>();

  const load = useCallback(async () => {
    setIsLoading(true);
    setError(undefined);
    try {
      setDetail(await getSalesConsumptionPostingDetail(postingRunId));
    } catch (loadError) {
      setError(getErrorMessage(loadError, 'No fue posible cargar el detalle del descuento.'));
    } finally {
      setIsLoading(false);
    }
  }, [postingRunId]);

  useEffect(() => { void load(); }, [load]);

  const canReverse = detail?.posting.status === 'COMPLETED';
  const isReversed = detail?.posting.status === 'REVERSED';

  return <Modal title="Detalle del descuento" isBusy={false} onClose={onClose} size="wide">
    {error && <div className="alert alert--error">{error}</div>}
    {isLoading && <div className="inline-loading"><span className="spinner" />Cargando detalle...</div>}
    {detail && !isLoading && <>
      <dl className="detail-list">
        <div><dt>Rango</dt><dd>{formatDateOnly(detail.posting.dateFrom)} – {formatDateOnly(detail.posting.dateTo)}</dd></div>
        <div><dt>Tienda</dt><dd>{detail.posting.storeName}</dd></div>
        <div><dt>Fecha de ejecución</dt><dd>{formatDateTime(detail.posting.createdAt, timeZone)}</dd></div>
        <div><dt>Usuario</dt><dd>{detail.posting.createdByName || 'Sin usuario'}</dd></div>
        <div><dt>Estado</dt><dd><PostingStatusPill status={detail.posting.status} /></dd></div>
        <div><dt>Costo real total</dt><dd>{formatOptionalMoney(detail.posting.totalRealCostDeducted)}</dd></div>
        {detail.posting.reversedAt && <div><dt>Revertido</dt><dd>{formatDateTime(detail.posting.reversedAt, timeZone)} por {detail.posting.reversedByName || 'Sin usuario'}</dd></div>}
        {detail.posting.reversalReason && <div><dt>Motivo de reversa</dt><dd>{detail.posting.reversalReason}</dd></div>}
      </dl>

      <div className="posting-detail-actions">
        {canReverse && <button className="button button--danger" onClick={() => setIsReversalModalOpen(true)} type="button">Revertir descuento</button>}
        {isReversed && <p className="posting-blocked-note">Esta corrida ya fue revertida.</p>}
      </div>

      {reversalResult && <div className={reversalResult.alreadyReversed ? 'alert alert--warning' : 'alert alert--success'}>{reversalResult.message}</div>}

      <h4>Insumos descontados</h4>
      {detail.ingredientSummary.length ? <div className="data-table-wrap"><table className="data-table consumption-table"><thead><tr><th>Insumo</th><th>Tienda</th><th>Cantidad descontada</th><th>Cantidad revertida</th><th>Neto</th><th>Costo real promedio</th><th>Total real</th><th>Estado</th></tr></thead><tbody>{detail.ingredientSummary.map((row, index) => <tr key={`${row.ingredientName}-${row.storeName}-${index}`}>
        <td>{row.ingredientName}</td>
        <td>{row.storeName}</td>
        <td>{formatQuantity(row.quantityDeducted)} {row.unitSymbol}</td>
        <td>{formatQuantity(row.quantityReversed)} {row.unitSymbol}</td>
        <td>{formatQuantity(row.netQuantity)} {row.unitSymbol}</td>
        <td>{formatOptionalMoney(row.realAverageUnitCost)}</td>
        <td>{formatOptionalMoney(row.totalRealCost)}</td>
        <td>{row.status === 'REVERSED' ? 'Revertido' : 'Aplicado'}</td>
      </tr>)}</tbody></table></div> : <div className="empty-panel empty-panel--small"><strong>Sin insumos descontados</strong></div>}

      <h4>Movimientos</h4>
      {detail.transactions.length ? <div className="data-table-wrap"><table className="data-table stock-table"><thead><tr><th>Tipo</th><th>Fecha</th><th>Insumo</th><th>Cantidad</th><th>Stock antes</th><th>Stock después</th><th>Referencia</th></tr></thead><tbody>{detail.transactions.map((transaction, index) => <tr key={index}>
        <td><span className={`stock-badge stock-badge--${transaction.reversalStatus === 'REVERSAL' ? 'entry' : 'out'}`}>{transaction.typeLabel}</span></td>
        <td>{formatDateTime(transaction.createdAt, timeZone)}</td>
        <td>{transaction.ingredientName}</td>
        <td>{formatQuantity(transaction.quantity)} {transaction.unitSymbol}</td>
        <td>{transaction.balanceBefore === null ? 'No registrado' : formatQuantity(transaction.balanceBefore)}</td>
        <td>{transaction.balanceAfter === null ? 'No registrado' : formatQuantity(transaction.balanceAfter)}</td>
        <td>{transaction.referenceLabel || 'Sin referencia'}{transaction.reversalStatus !== 'ACTIVE' && <ReversalStatusNote status={transaction.reversalStatus} />}</td>
      </tr>)}</tbody></table></div> : <div className="empty-panel empty-panel--small"><strong>Sin movimientos</strong></div>}

      {detail.receiptDetails && detail.receiptDetails.length > 0 && <>
        <h4>Tickets relacionados</h4>
        <div className="data-table-wrap"><table className="data-table consumption-receipts-table"><thead><tr><th>Ticket</th><th>Fecha</th><th>Producto</th><th>Cantidad vendida</th><th>Insumos</th></tr></thead><tbody>{detail.receiptDetails.map((receipt, index) => <tr key={index}>
          <td>{receipt.receiptNumber || 'Sin número'}</td>
          <td>{formatOptionalDate(receipt.receiptDate)}</td>
          <td><strong>{receipt.productName}</strong>{receipt.variantName && <small>{receipt.variantName}</small>}</td>
          <td>{receipt.quantitySold ? formatQuantity(receipt.quantitySold) : '—'}</td>
          <td><ul className="preview-ingredient-list">{receipt.ingredients.map((ingredient, ingredientIndex) => <li key={ingredientIndex}>{ingredient.ingredientName}: {formatQuantity(ingredient.quantity)} {ingredient.unitSymbol}</li>)}</ul></td>
        </tr>)}</tbody></table></div>
      </>}
    </>}

    {isReversalModalOpen && detail && <ReversalConfirmModal
      detail={detail}
      onClose={() => setIsReversalModalOpen(false)}
      onReversed={(result) => { setReversalResult(result); setIsReversalModalOpen(false); void load(); onReversed(); }}
    />}
  </Modal>;
}

function ReversalConfirmModal({ detail, onClose, onReversed }: { detail: SalesPostingDetailResponse; onClose: () => void; onReversed: (result: SalesPostingReversalResult) => void }) {
  const [confirmText, setConfirmText] = useState('');
  const [reason, setReason] = useState('');
  const [isReversing, setIsReversing] = useState(false);
  const [error, setError] = useState<string>();

  const canConfirm = confirmText === REVERSAL_CONFIRM_WORD && reason.trim().length >= MIN_REASON_LENGTH;

  const confirmReversal = async () => {
    if (!canConfirm || isReversing) return;
    setIsReversing(true);
    setError(undefined);
    try {
      const result = await reverseSalesConsumptionPosting(detail.posting.id, { confirm: true, confirmationText: confirmText, reason: reason.trim() });
      onReversed(result);
    } catch (reverseError) {
      setError(getErrorMessage(reverseError, 'No fue posible revertir este descuento.'));
    } finally {
      setIsReversing(false);
    }
  };

  return <Modal
    title="Confirmar reversa de descuento"
    isBusy={isReversing}
    onClose={onClose}
    footer={<>
      <button className="button button--ghost" disabled={isReversing} onClick={onClose} type="button">Cancelar</button>
      <button className="button button--danger" disabled={isReversing || !canConfirm} onClick={() => void confirmReversal()} type="button">{isReversing ? 'Revirtiendo...' : 'Confirmar reversa'}</button>
    </>}
  >
    {error && <div className="alert alert--error">{error}</div>}
    <dl className="detail-list">
      <div><dt>Rango</dt><dd>{formatDateOnly(detail.posting.dateFrom)} – {formatDateOnly(detail.posting.dateTo)}</dd></div>
      <div><dt>Tienda</dt><dd>{detail.posting.storeName}</dd></div>
      <div><dt>Movimientos a revertir</dt><dd>{detail.posting.totalTransactionsCreated}</dd></div>
      <div><dt>Costo real relacionado</dt><dd>{formatOptionalMoney(detail.posting.totalRealCostDeducted)}</dd></div>
    </dl>
    <div className="alert alert--warning">Esta acción devolverá al inventario las cantidades descontadas por esta corrida. No borrará los movimientos originales; creará movimientos de reversa para mantener auditoría.</div>
    <label className="field">
      <span>Motivo de la reversa (mínimo 10 caracteres)</span>
      <textarea onChange={(event) => setReason(event.target.value)} rows={3} value={reason} />
    </label>
    <label className="field">
      <span>Escribe {REVERSAL_CONFIRM_WORD} para confirmar</span>
      <input onChange={(event) => setConfirmText(event.target.value)} placeholder={REVERSAL_CONFIRM_WORD} value={confirmText} />
    </label>
  </Modal>;
}

function ReversalStatusNote({ status }: { status: SalesPostingTransactionReversalStatus }) {
  if (status === 'REVERSAL') return <small>Reversa</small>;
  if (status === 'REVERSED') return <small>Revertido</small>;
  return null;
}

function PostingStatusPill({ status }: { status: SalesPostingRunStatus }) {
  const label = { RUNNING: 'Pendiente', COMPLETED: 'Aplicado', FAILED: 'Fallido', REVERSED: 'Revertido' }[status];
  const tone = { RUNNING: 'pending', COMPLETED: 'applied', FAILED: 'failed', REVERSED: 'reverted' }[status];
  return <span className={`consumption-status consumption-status--${tone}`}>{label}</span>;
}

function formatDateTime(value: string, timeZone: string = DEFAULT_STORE_TIMEZONE) { return new Intl.DateTimeFormat('es-MX', { dateStyle: 'medium', timeStyle: 'short', timeZone }).format(new Date(value)); }
function formatDateOnly(value: string) { return new Intl.DateTimeFormat('es-MX', { dateStyle: 'medium', timeZone: 'UTC' }).format(new Date(value.length === 10 ? `${value}T00:00:00.000Z` : value)); }
function formatOptionalDate(value: string | null) { return value ? formatDateTime(value) : 'Sin fecha'; }
